import { useState } from 'react';
import { Link } from 'react-router-dom';
import { listerMetiers } from '@/api/metiers';
import { obtenirReferentiels } from '@/api/activites';
import { useFetch } from '@/hooks/useFetch';
import { SearchBar } from '@/components/SearchBar';
import { FiltreSelect } from '@/components/FiltreSelect';
import { lireCodeFormacode } from '@/utils/rechercheFormacode';
import { MetierCard } from '@/components/MetierCard';
import { Pagination } from '@/components/Pagination';
import { Loader } from '@/components/Loader';
import { ErrorMessage } from '@/components/ErrorMessage';
import { libelleFamille } from '@/utils/format';

export function MetiersPage() {
  const [recherche, setRecherche] = useState('');
  const [famille, setFamille] = useState('');
  const [dossier, setDossier] = useState('');
  const [rome, setRome] = useState('');
  const [saisieFormacode, setSaisieFormacode] = useState('');
  const [page, setPage] = useState(1);
  const formacode = lireCodeFormacode(saisieFormacode);

  const referentiels = useFetch((signal) => obtenirReferentiels(signal), []);
  const metiers = useFetch(
    (signal) =>
      listerMetiers(
        {
          search: recherche,
          famille,
          dossier: dossier ? Number(dossier) : undefined,
          rome,
          formacode: formacode.code,
          page,
          limit: 24,
        },
        signal,
      ),
    [recherche, famille, dossier, rome, formacode.code, page],
  );

  // Un changement de filtre doit ramener à la page 1, sinon on affiche une page
  // hors bornes du nouveau jeu de résultats.
  const changerFiltre = (setter: (v: string) => void) => (valeur: string) => {
    setter(valeur);
    setPage(1);
  };

  return (
    <div className="page">
      <div className="fiche__entete-ligne">
        <h1>Métiers</h1>
        <Link to="/metiers/nouveau" className="bouton--export">
          + Créer une fiche métier
        </Link>
      </div>

      <div className="barre-filtres">
        <SearchBar
          valeur={recherche}
          onChange={changerFiltre(setRecherche)}
          placeholder="Rechercher un métier ou un code (D192)…"
        />
        <FiltreSelect
          label="Famille"
          valeur= {famille}
          onChange={changerFiltre(setFamille)}
          options={(referentiels.donnees?.famillesMetier ?? []).map((f) => ({
            valeur: f.codeFamille,
            libelle: libelleFamille(f),
          }))}
        />
        <FiltreSelect
          label="Fiche ROME"
          valeur={rome}
          onChange={changerFiltre(setRome)}
          // Seuls les codes réellement portés par une fiche : le référentiel en compte
          // 1 911, en proposer autant reviendrait à offrir surtout des filtres vides.
          options={(referentiels.donnees?.rome ?? [])
            .filter((r) => r.nbMetiers > 0)
            .map((r) => ({
              valeur: r.codeRome,
              libelle: r.libelle ? `${r.codeRome} — ${r.libelle}` : r.codeRome,
            }))}
        />
        <FiltreSelect
          label="Dossier"
          valeur={dossier}
          onChange={changerFiltre(setDossier)}
          options={(referentiels.donnees?.dossiersSource ?? []).map((d) => ({
            valeur: String(d.id),
            libelle: d.libelle,
          }))}
        />
        <SearchBar
          valeur={saisieFormacode}
          onChange={changerFiltre(setSaisieFormacode)}
          placeholder="Code Formacode exact (ex. 31407)"
        />
      </div>
      {formacode.erreur && <p className="detail">{formacode.erreur}</p>}

      {metiers.erreur && <ErrorMessage message={metiers.erreur} />}
      {metiers.chargement && <Loader />}

      {metiers.donnees && (
        <>
          <div className="grille">
            {metiers.donnees.data.map((m) => (
              <MetierCard key={m.codeMetier} metier={m} />
            ))}
          </div>

          {metiers.donnees.data.length === 0 && <p className="vide">Aucun métier ne correspond.</p>}

          <Pagination pagination={metiers.donnees.pagination} onChangePage={setPage} />
        </>
      )}
    </div>
  );
}
